/**
 * Atelio landing page — atelio.altuzar.dev
 *
 * Editorial gallery aesthetic: warm paper, ink, terracotta, Fraunces display.
 * Bilingual ES-first; ?lang=en or the toggle switches language (and updates
 * <html lang>). Built from marketing/landing-page-blueprint.md.
 */
import { useEffect, useMemo, useState } from 'react'

type Lang = 'es' | 'en'

const APP_URL = import.meta.env.VITE_APP_LAUNCH_URL || 'https://app.atelio.altuzar.dev'

const copy = {
  es: {
    nav: { how: 'Cómo funciona', pricing: 'Precios', faq: 'Preguntas', cta: 'Entrar' },
    hero: {
      kicker: 'Para artistas que aplican a escuelas, becas y residencias',
      headline: 'Tu obra merece mejores palabras.',
      sub: 'Sube de 3 a 5 piezas. Atelio mira cada obra y redacta las notas por pieza y el artist statement para tu aplicación — en tu voz, listos para editar y enviar.',
      cta: 'Empieza con tu obra',
      secondary: 'Ver un ejemplo',
      proof: 'Construido con artistas en CDMX',
      specimenLabel: 'Nota generada · 148 / 150 palabras',
      specimen:
        '“En ‘Marea baja’ dejé que el lino crudo hablara por mí — el óleo diluido se hunde en la trama hacia el horizonte mientras el primer plano conserva trazos gruesos y arrastrados de blanco de zinc. Es la pintura más paciente de esta serie, y la que me enseñó a detenerme antes.”',
      specimenPiece: 'Marea baja — óleo sobre lino, 2024',
    },
    strip: 'Convocatorias · Residencias · Posgrados · Becas — un solo flujo, en español o inglés.',
    pain: {
      headline: 'Escribir sobre tu obra no debería doler más que hacerla.',
      items: [
        {
          title: 'La convocatoria cierra el viernes',
          body: 'Y las notas de obra siguen en blanco. Cada programa pide otro enfoque, otro límite de palabras, otro idioma.',
        },
        {
          title: 'ChatGPT suena a folleto',
          body: '“Explora la intersección de lo efímero…” — el jurado lo ha leído mil veces, y ninguna de esas frases es tuya.',
        },
        {
          title: 'Nadie más ha visto la obra',
          body: 'Un texto genérico se nota porque podría hablar de cualquier pieza. El tuyo tiene que hablar de la tuya.',
        },
      ],
    },
    moat: {
      headline: 'Atelio conoce tu obra. Literalmente.',
      sub: 'Cuando subes una pieza, Atelio la mira una vez y guarda una descripción visual detallada — materiales, composición, gesto, paleta. Cada texto se escribe desde ahí. Por eso una nota de Atelio no se puede pegar en otra obra sin que se note.',
      genericLabel: 'IA genérica',
      generic:
        '“Esta obra explora la intersección entre la memoria y el paisaje, invitando al espectador a un espacio liminal de contemplación…”',
      atelioLabel: 'Atelio',
      atelio:
        '“El óleo diluido se hunde en la trama del lino crudo hacia el horizonte; el primer plano conserva trazos gruesos y arrastrados de blanco de zinc…”',
      footnote: 'Los detalles subrayados existen en la pieza. Esa es la diferencia.',
      features: [
        {
          title: 'Mira cada pieza una vez',
          body: 'Una pasada de visión por obra. Lo que Atelio ve queda guardado — y tú puedes editarlo.',
        },
        {
          title: 'Redacta para ese programa',
          body: 'Pegas la convocatoria: nombre, prompt y límite de palabras. Notas + statement, en ES o EN, dentro del límite.',
        },
        {
          title: 'Tú firmas, tú decides',
          body: 'Todo es editable, con contador de palabras en vivo. Exportas un documento limpio, listo para pegar.',
        },
      ],
    },
    how: {
      headline: 'Tres pasos antes del deadline',
      steps: [
        { title: 'Sube tu obra', body: 'De 3 a 5 piezas. Atelio las lee al momento.' },
        { title: 'Pega la convocatoria', body: 'El prompt del programa, el límite de palabras y el idioma.' },
        { title: 'Edita y envía', body: 'Notas por pieza + statement en tu voz. Exporta y pega.' },
      ],
    },
    pricing: {
      headline: 'Un instrumento de pago. Sin plan gratis.',
      sub: 'Cada generación redacta todas las notas y el statement de una aplicación. Si una generación falla, no se cobra.',
      pack: {
        name: 'Paquete de créditos',
        price: '$9',
        unit: 'USD · pago único',
        line: '10 generaciones. Tuyas para siempre.',
        tagline: 'Para esta convocatoria.',
        cta: 'Comprar paquete',
      },
      yearly: {
        name: 'Pase anual',
        price: '$120',
        unit: 'USD / año',
        line: 'Generaciones ilimitadas.',
        tagline: 'Para todas las del año.',
        cta: 'Ir a ilimitado',
        badge: 'Mejor valor',
      },
    },
    faq: {
      headline: 'Preguntas honestas',
      items: [
        {
          q: '¿Esto no es hacer trampa?',
          a: 'No. Atelio redacta un borrador a partir de TU obra y TUS datos; tú lo editas y tú lo firmas. Es la diferencia entre un ghostwriter que te conoce y copiar un texto ajeno.',
        },
        {
          q: '¿En qué se diferencia de ChatGPT?',
          a: 'Atelio mira cada pieza y guarda una descripción visual detallada; cada texto se escribe desde lo que de verdad hay en la obra y se reorienta al programa específico. Sin eso, cualquier texto suena a folleto.',
        },
        {
          q: '¿Funciona en español?',
          a: 'Sí, nativo. Cambias entre español e inglés con un toque y Atelio vuelve a redactar en el otro idioma.',
        },
        {
          q: '¿Qué pasa si una generación falla?',
          a: 'Se devuelve el crédito automáticamente. Nunca pagas por un texto que no llegó.',
        },
        {
          q: '¿Qué pasa con mis imágenes?',
          a: 'Son tuyas. Se usan únicamente para escribir tus notas; no entrenamos modelos con tu obra.',
        },
        {
          q: '¿Hay plan gratis?',
          a: 'No. Atelio es un instrumento de trabajo: $9 te dan 10 aplicaciones redactadas. Una beca paga eso unas mil veces.',
        },
      ],
    },
    final: {
      headline: 'La próxima convocatoria no se va a escribir sola.',
      sub: 'Sube tu obra hoy y llega al deadline con las palabras listas.',
      cta: 'Empieza con tu obra',
      risk: 'Sin suscripción obligatoria · las generaciones fallidas no se cobran',
    },
    footer: { contact: 'hola@altuzar.com', rights: 'Atelio. Hecho para artistas.' },
  },
  en: {
    nav: { how: 'How it works', pricing: 'Pricing', faq: 'FAQ', cta: 'Open app' },
    hero: {
      kicker: 'For artists applying to schools, grants, and residencies',
      headline: 'Your work deserves better words.',
      sub: 'Upload 3–5 pieces. Atelio looks at each work and drafts the per-piece notes and artist statement for your application — in your voice, ready to edit and send.',
      cta: 'Start with your work',
      secondary: 'See an example',
      proof: 'Built with artists in Mexico City',
      specimenLabel: 'Generated note · 148 / 150 words',
      specimen:
        '“In ‘Low Tide’ I let the raw linen do half the talking — thinned oil sinks into the weave at the horizon while the foreground keeps thick, dragged strokes of zinc white. It is the most patient painting in this series, and the one that taught me to stop earlier.”',
      specimenPiece: 'Low Tide — oil on linen, 2024',
    },
    strip: 'Open calls · Residencies · Grad programs · Grants — one flow, in Spanish or English.',
    pain: {
      headline: 'Writing about your work shouldn’t hurt more than making it.',
      items: [
        {
          title: 'The call closes Friday',
          body: 'And the piece notes are still blank. Every program wants a different angle, word limit, and language.',
        },
        {
          title: 'ChatGPT sounds like a brochure',
          body: '“This work explores the intersection of…” — the jury has read it a thousand times, and none of it is you.',
        },
        {
          title: 'No one else has seen the work',
          body: 'Generic text shows because it could describe any piece. Yours has to describe yours.',
        },
      ],
    },
    moat: {
      headline: 'Atelio knows your work. Literally.',
      sub: 'When you upload a piece, Atelio looks at it once and stores a detailed visual description — materials, composition, gesture, palette. Every text is written from that. That is why an Atelio note can’t be pasted onto another artwork without anyone noticing.',
      genericLabel: 'Generic AI',
      generic:
        '“This work explores the intersection of memory and landscape, inviting the viewer into a liminal space of contemplation…”',
      atelioLabel: 'Atelio',
      atelio:
        '“Thinned oil sinks into the weave of raw linen toward the horizon; the foreground keeps thick, dragged strokes of zinc white…”',
      footnote: 'The underlined details exist in the piece. That’s the difference.',
      features: [
        {
          title: 'Looks at each piece once',
          body: 'One vision pass per work. What Atelio sees is stored — and you can edit it.',
        },
        {
          title: 'Drafts for that program',
          body: 'Paste the call: name, prompt, word limit. Notes + statement, in ES or EN, within the limit.',
        },
        {
          title: 'You sign, you decide',
          body: 'Everything is editable with a live word counter. Export a clean, paste-ready document.',
        },
      ],
    },
    how: {
      headline: 'Three steps before the deadline',
      steps: [
        { title: 'Upload your work', body: '3 to 5 pieces. Atelio reads them on the spot.' },
        { title: 'Paste the call', body: 'The program’s prompt, word limit, and language.' },
        { title: 'Edit and send', body: 'Per-piece notes + statement in your voice. Export and paste.' },
      ],
    },
    pricing: {
      headline: 'A paid instrument. No free tier.',
      sub: 'Each generation drafts every note and the statement for one application. If a generation fails, it’s never charged.',
      pack: {
        name: 'Credit pack',
        price: '$9',
        unit: 'USD · one-time',
        line: '10 generations. Yours forever.',
        tagline: 'For this open call.',
        cta: 'Buy the pack',
      },
      yearly: {
        name: 'Yearly pass',
        price: '$120',
        unit: 'USD / year',
        line: 'Unlimited generations.',
        tagline: 'For every call this year.',
        cta: 'Go unlimited',
        badge: 'Best value',
      },
    },
    faq: {
      headline: 'Honest questions',
      items: [
        {
          q: 'Isn’t this cheating?',
          a: 'No. Atelio drafts from YOUR work and YOUR details; you edit it and you sign it. It’s the difference between a ghostwriter who knows you and copying someone else’s text.',
        },
        {
          q: 'How is it different from ChatGPT?',
          a: 'Atelio looks at each piece and stores a detailed visual description; every text is written from what is actually in the work and retargeted to the specific program. Without that, everything sounds like a brochure.',
        },
        {
          q: 'Does it work in Spanish?',
          a: 'Natively. Toggle between Spanish and English and Atelio re-drafts in the other language.',
        },
        {
          q: 'What if a generation fails?',
          a: 'The credit is refunded automatically. You never pay for text that didn’t arrive.',
        },
        {
          q: 'What about my images?',
          a: 'They’re yours. They are used only to write your notes; we don’t train models on your work.',
        },
        {
          q: 'Is there a free plan?',
          a: 'No. Atelio is a working instrument: $9 buys 10 drafted applications. One grant pays for that a thousand times over.',
        },
      ],
    },
    final: {
      headline: 'The next open call won’t write itself.',
      sub: 'Upload your work today and reach the deadline with the words ready.',
      cta: 'Start with your work',
      risk: 'No subscription required · failed generations are never charged',
    },
    footer: { contact: 'hola@altuzar.com', rights: 'Atelio. Made for artists.' },
  },
} as const

function CtaButton({ children, href, big = false }: { children: React.ReactNode; href: string; big?: boolean }) {
  return (
    <a
      href={href}
      className={`inline-block bg-ink text-paper font-sans font-semibold tracking-wide rounded-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg ${
        big ? 'px-10 py-4 text-lg' : 'px-7 py-3 text-base'
      }`}
    >
      {children}
    </a>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-inksoft">{children}</p>
  )
}

export function AtelioLanding() {
  const initialLang: Lang = useMemo(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('lang')
    if (fromQuery === 'en' || fromQuery === 'es') return fromQuery
    return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'es'
  }, [])
  const [lang, setLang] = useState<Lang>(initialLang)
  const t = copy[lang]

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  return (
    <div className="min-h-screen bg-paper text-ink font-sans antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-20 border-b border-hairline bg-paper/95 backdrop-blur-sm">
        <nav className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <a href="#" className="font-display text-2xl font-semibold tracking-tight">
            Atelio
          </a>
          <div className="flex items-center gap-6">
            <a href="#how" className="hidden text-sm text-inksoft hover:text-ink sm:block">
              {t.nav.how}
            </a>
            <a href="#pricing" className="hidden text-sm text-inksoft hover:text-ink sm:block">
              {t.nav.pricing}
            </a>
            <a href="#faq" className="hidden text-sm text-inksoft hover:text-ink sm:block">
              {t.nav.faq}
            </a>
            <div className="flex overflow-hidden rounded-sm border border-ink" role="radiogroup" aria-label="Language">
              {(['es', 'en'] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setLang(option)}
                  role="radio"
                  aria-checked={lang === option}
                  className={`min-h-[40px] px-3 text-xs font-semibold ${
                    lang === option ? 'bg-ink text-paper' : 'text-ink hover:bg-wash'
                  }`}
                >
                  {option.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href={APP_URL}
              className="rounded-sm bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              {t.nav.cta}
            </a>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pb-20 pt-16 sm:pt-24">
        <div className="max-w-3xl">
          <SectionLabel>{t.hero.kicker}</SectionLabel>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight sm:text-7xl">
            {t.hero.headline}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-inksoft">{t.hero.sub}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <CtaButton href={APP_URL} big>
              {t.hero.cta}
            </CtaButton>
            <a href="#moat" className="px-2 py-3 text-base font-medium text-accent underline underline-offset-4">
              {t.hero.secondary}
            </a>
          </div>
          <p className="mt-6 inline-block rounded-full border border-hairline bg-card px-4 py-1.5 text-xs font-medium text-inksoft">
            {t.hero.proof}
          </p>
        </div>

        {/* Specimen: the artifact is the demo */}
        <figure className="mt-16 grid gap-0 overflow-hidden rounded-sm border border-hairline bg-card shadow-sm sm:grid-cols-5">
          <div className="flex items-center justify-center bg-wash p-10 sm:col-span-2">
            <div
              aria-hidden
              className="aspect-[4/5] w-full max-w-[240px] border-[10px] border-card bg-gradient-to-b from-[#C8BDA8] via-[#8FA3A8] to-[#3E4A52] shadow-md"
            />
          </div>
          <figcaption className="flex flex-col justify-center gap-4 p-8 sm:col-span-3 sm:p-12">
            <p className="font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
              {t.hero.specimenLabel}
            </p>
            <blockquote className="font-display text-xl italic leading-relaxed">{t.hero.specimen}</blockquote>
            <p className="text-sm text-inksoft">{t.hero.specimenPiece}</p>
          </figcaption>
        </figure>
      </section>

      {/* Trust strip */}
      <div className="border-y border-hairline bg-wash">
        <p className="mx-auto max-w-5xl px-6 py-4 text-center text-sm font-medium text-inksoft">{t.strip}</p>
      </div>

      {/* Pain */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-4xl">
          {t.pain.headline}
        </h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-sm border border-hairline bg-hairline sm:grid-cols-3">
          {t.pain.items.map((item) => (
            <div key={item.title} className="bg-card p-8">
              <h3 className="font-display text-xl font-semibold">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-inksoft">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Moat: before/after */}
      <section id="moat" className="border-y border-hairline bg-card">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <SectionLabel>{lang === 'es' ? 'La diferencia' : 'The difference'}</SectionLabel>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-4xl">
            {t.moat.headline}
          </h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-inksoft">{t.moat.sub}</p>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <div className="rounded-sm border border-hairline bg-paper p-8 opacity-80">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-inksoft">
                {t.moat.genericLabel}
              </p>
              <p className="mt-4 font-display text-lg italic leading-relaxed text-inksoft">{t.moat.generic}</p>
            </div>
            <div className="rounded-sm border-2 border-accent bg-paper p-8">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
                {t.moat.atelioLabel}
              </p>
              <p className="mt-4 font-display text-lg italic leading-relaxed">{t.moat.atelio}</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-inksoft">{t.moat.footnote}</p>

          <div className="mt-14 grid gap-10 sm:grid-cols-3">
            {t.moat.features.map((feature, index) => (
              <div key={feature.title}>
                <p className="font-display text-4xl font-semibold text-accent">{index + 1}</p>
                <h3 className="mt-2 font-display text-lg font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-inksoft">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">{t.how.headline}</h2>
        <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-hairline bg-hairline sm:grid-cols-3">
          {t.how.steps.map((step, index) => (
            <li key={step.title} className="bg-paper p-8">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.2em] text-accent">
                {lang === 'es' ? 'Paso' : 'Step'} {index + 1}
              </p>
              <h3 className="mt-2 font-display text-xl font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-inksoft">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-hairline bg-card">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">{t.pricing.headline}</h2>
          <p className="mt-4 max-w-2xl text-inksoft">{t.pricing.sub}</p>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 sm:items-stretch">
            <div className="flex flex-col rounded-sm border border-hairline bg-paper p-10">
              <h3 className="font-display text-2xl font-semibold">{t.pricing.pack.name}</h3>
              <p className="mt-1 text-sm italic text-inksoft">{t.pricing.pack.tagline}</p>
              <p className="mt-6 font-display text-5xl font-semibold">
                {t.pricing.pack.price}
                <span className="ml-2 align-middle font-sans text-sm font-normal text-inksoft">
                  {t.pricing.pack.unit}
                </span>
              </p>
              <p className="mt-4 text-sm text-inksoft">{t.pricing.pack.line}</p>
              <div className="mt-8">
                <a
                  href={APP_URL}
                  className="inline-block rounded-sm border border-ink px-7 py-3 font-semibold hover:bg-wash"
                >
                  {t.pricing.pack.cta}
                </a>
              </div>
            </div>

            <div className="relative flex flex-col rounded-sm border-2 border-ink bg-paper p-10">
              <span className="absolute -top-3 left-8 rounded-sm bg-accent px-3 py-1 text-xs font-semibold text-white">
                {t.pricing.yearly.badge}
              </span>
              <h3 className="font-display text-2xl font-semibold">{t.pricing.yearly.name}</h3>
              <p className="mt-1 text-sm italic text-inksoft">{t.pricing.yearly.tagline}</p>
              <p className="mt-6 font-display text-5xl font-semibold">
                {t.pricing.yearly.price}
                <span className="ml-2 align-middle font-sans text-sm font-normal text-inksoft">
                  {t.pricing.yearly.unit}
                </span>
              </p>
              <p className="mt-4 text-sm text-inksoft">{t.pricing.yearly.line}</p>
              <div className="mt-8">
                <CtaButton href={APP_URL}>{t.pricing.yearly.cta}</CtaButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-6 py-20">
        <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">{t.faq.headline}</h2>
        <div className="mt-10 divide-y divide-hairline border-y border-hairline">
          {t.faq.items.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-semibold">
                {item.q}
                <span aria-hidden className="text-accent transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-inksoft">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-hairline bg-ink text-paper">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="font-display text-4xl font-semibold leading-tight sm:text-5xl">{t.final.headline}</h2>
          <p className="mt-5 text-lg text-paper/70">{t.final.sub}</p>
          <div className="mt-10">
            <a
              href={APP_URL}
              className="inline-block rounded-sm bg-accent px-10 py-4 text-lg font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              {t.final.cta}
            </a>
          </div>
          <p className="mt-6 text-xs text-paper/50">{t.final.risk}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ink text-paper/60">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 border-t border-paper/10 px-6 py-8 text-sm">
          <p className="font-display text-lg font-semibold text-paper">Atelio</p>
          <div className="flex gap-6">
            <a href={APP_URL} className="hover:text-paper">
              App
            </a>
            <a href="#pricing" className="hover:text-paper">
              {t.nav.pricing}
            </a>
            <a href="#faq" className="hover:text-paper">
              {t.nav.faq}
            </a>
            <a href={`mailto:${t.footer.contact}`} className="hover:text-paper">
              {t.footer.contact}
            </a>
          </div>
          <p>
            © {new Date().getFullYear()} {t.footer.rights}
          </p>
        </div>
      </footer>
    </div>
  )
}
